'use client';

import React from 'react';
import PopupTemplate from '@/app/ui/popupTemplate';
import SubscriptionFrontend from './subscriptionFrontend';
import { safePathFromCallback } from '../utils/callbackSecurity';

interface SubscriptionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  callbackBaseUrl?: string; // URL to return to after payment completion
}

/**
 * SubscriptionPopup component with dynamic callback URL support
 * 
 * Usage examples:
 * 
 * // For profile page (default behavior)
 * <SubscriptionPopup 
 *   isOpen={showSubscriptionPopup} 
 *   onClose={() => setShowSubscriptionPopup(false)} 
 *   callbackBaseUrl={'/profile'}
 * />
 * 
 * // For chat page - preserve chat session
 * <SubscriptionPopup 
 *   isOpen={showSubscriptionPopup} 
 *   onClose={() => setShowSubscriptionPopup(false)} 
 *   callbackBaseUrl={window.location.pathname + window.location.search}
 * />
 * 
 * // For subscription page (no callback needed)
 * <SubscriptionPopup 
 *   isOpen={showSubscriptionPopup} 
 *   onClose={() => setShowSubscriptionPopup(false)} 
 * />
 */

/**
 * Utility function to get current URL for callback purposes
 * Use this in chat pages or any page where you want to return after payment
 * This function ensures the returned URL is safe for redirects
 */
export function getCurrentUrlForCallback(): string {
  if (typeof window !== 'undefined') {
    const currentUrl = window.location.pathname + window.location.search;
    // Validate the URL to ensure it's safe
    return safePathFromCallback(currentUrl) || '/';
  }
  return '/';
}

export default function SubscriptionPopup({ isOpen, onClose, callbackBaseUrl = '/profile' }: SubscriptionPopupProps) {
  return (
    <PopupTemplate
      isOpen={isOpen}
      onClose={onClose}
      closeable={true}
      isTransparent={true}
      maxWidth="max-w-[40rem]"
      className="max-h-[70vh] overflow-hidden"
    >
    <SubscriptionFrontend callbackBaseUrl={callbackBaseUrl} />
    </PopupTemplate>
  );
}
